import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { RootState } from '../store';
import { User } from '../types';

const emptyForm = { name: '', email: '', password: '' };

export const SellersPage: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth);
  const isAdmin = useMemo(() => {
    const role = (user?.role || '').toLowerCase();
    return role === 'admin' || role === 'manager';
  }, [user?.role]);

  const [items, setItems] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; isActive: boolean; password: string }>({ name: '', isActive: true, password: '' });
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getUsers({ role: 'Seller' });
      setItems(res.items || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron obtener los vendedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const canCreate = form.name.trim().length > 2 && form.email.trim().length > 3 && form.password.length >= 6;

  const submit = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError(null);
    setSuccess(null);
    try {
      await api.createUser({ ...form, role: 'Seller' });
      setForm(emptyForm);
      setSuccess('Vendedor creado correctamente');
      setCreateOpen(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al crear vendedor');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (u: User) => {
    setEditing(u);
    setEditForm({ name: u.name, isActive: u.isActive, password: '' });
    setEditOpen(true);
    setError(null);
    setSuccess(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.updateUser(editing.id, {
        name: editForm.name,
        isActive: editForm.isActive,
        password: editForm.password || undefined,
      });
      setSuccess('Vendedor actualizado');
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al actualizar vendedor');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteUser = async (id: number) => {
    if (!window.confirm('¿Eliminar este vendedor?')) return;
    try {
      await api.deleteUser(id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo eliminar');
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">Tu rol no tiene acceso a la gestión de vendedores.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Vendedores</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setForm(emptyForm); setCreateOpen(true); }}>Nuevo vendedor</Button>
          <Button variant="outlined" onClick={load}>Refrescar</Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Listado</Typography>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
          {loading ? (
            <Typography variant="body2" color="text.secondary">Cargando...</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Último acceso</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Chip size="small" label={u.isActive ? 'Activo' : 'Inactivo'} color={u.isActive ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => deleteUser(u.id)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">Sin vendedores registrados.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar vendedor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
            <TextField label="Contraseña" type="password" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} helperText="Completa para resetear" />
            <TextField
              select
              label="Estado"
              value={editForm.isActive ? '1' : '0'}
              onChange={e => setEditForm({ ...editForm, isActive: e.target.value === '1' })}
            >
              <MenuItem value="1">Activo</MenuItem>
              <MenuItem value="0">Inactivo</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
        <Button variant="contained" onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Guardando...' : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Nuevo vendedor</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <TextField label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
          <TextField label="Contraseña" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required helperText="Mínimo 6 caracteres" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!canCreate || creating}>{creating ? 'Guardando...' : 'Crear'}</Button>
      </DialogActions>
    </Dialog>
  </Box>
  );
};
